import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import dayjs from "dayjs";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Tooltip,
} from "@mui/material";
import Textarea from "@mui/joy/Textarea";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { TextInputComponent } from "../../../../../../common/components";
import DatePickerComponent from "../../../../../../common/components/date-picker/date-picker-compoenent";
import {
  addIssueRisk,
  updateIssueRisk,
} from "../../../../../../api/issue-risk";
import CloseIcon from "../../../../../../assets/icons/closeIcon.svg";
import "./company-dashboard-initiative-drawer-issue-add-popup.scss";
import { Close } from "@mui/icons-material";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

export interface StatusOption {
  statusId: string;
  statusName: string;
}

const CompanyDashboardInitiativeIssueAddPopup: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    editData,
    addPopupTitle,
    fetchInitiativeById,
    setLoading,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    initiativeId,
  } = props;

  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");

  const userName =
    userDetails?.firstName || "" + " " + userDetails?.lastName || "";

  const statusList: StatusOption[] = [
    { statusId: "Active", statusName: "Active" },
    { statusId: "Resolved", statusName: "Resolved" },
  ];

  const handleCreateRiskIssue = async (payload: any) => {
    setLoading(true);
    try {
      await (editData?.id
        ? updateIssueRisk(editData.id, payload)
        : addIssueRisk(payload));
      fetchInitiativeById();
      if (editData?.id) {
        setAlertMessage("Risk Issue updated successfully");
      } else {
        setAlertMessage("Risk Issue created successfully");
      }
      setAlertType("success");
      setAlertOpen(true);
      setLoading(false);
      onCancel();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Risk Issue",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
      if (error?.response?.status === 404) {
        fetchInitiativeById();
        onCancel();
      }
    }
  };

  const validationSchema = () =>
    Yup.object({
      mitigationPlan: Yup.string()
        .required("Mitigation Plan is required")
        .max(1000, "Description cannot exceed 1000 characters"),
      description: Yup.string()
        .required("Description is required")
        .max(1000, "Description cannot exceed 1000 characters"),
      status: Yup.mixed().required("Status is required"),
      asOf: Yup.date().nullable().required("As of is required"),
    });

  const slotsPaperStatus = (props: any) => {
    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
          }}
        >
          <Close
            sx={{
              cursor: "pointer",
              color: "#555",
              margin: "4px 0",
              "&:hover": { color: "#000" },
            }}
            onClick={(e) => {
              e.stopPropagation();
              const dropdown = document.querySelector(
                ".MuiAutocomplete-popper",
              ) as HTMLElement;
              if (dropdown) dropdown.style.display = "none";
            }}
          />
        </div>

        {props.children}
      </div>
    );
  };
  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-initiative-drawer-add-disposition`}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "#f7f8fb",
        },
      }}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">{addPopupTitle}</div>
          <div className="image-wrapper">
            <button
              style={{ background: "transparent", border: "none", padding: 0 }}
              onClick={onCancel}
            >
              <img
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
                alt="close"
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Formik
            initialValues={{
              asOf: dayjs(editData?.asOf) || dayjs(),
              openedBy: editData?.openedBy || userName || "",
              mitigationPlan: editData?.mitigationPlan || "",
              description: editData?.description || "",
              status: {
                statusId: editData?.status || "Active",
                statusName: editData?.status || "Active",
              },
              isShowDescription: false,
              isShowMitigationPlan: false,
            }}
            validationSchema={validationSchema}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={(values) => {
              const newIssue = {
                asOf: values.asOf,
                description: values.description,
                initiativeId: initiativeId,
                mitigationPlan: values.mitigationPlan,
                status: values.status?.statusName || "Active",
              };
              handleCreateRiskIssue(newIssue);
            }}
          >
            {({
              values,
              setFieldValue,
              touched,
              errors,
              isValid,
              submitCount,
            }) => {
              const charCount = values.description?.length || 0;
              const remaining = 1000 - charCount;
              const charCountMitigation = values.mitigationPlan?.length || 0;
              const remainingMitigation = 1000 - charCountMitigation;
              return (
                <Form>
                  <OverlayScrollbarsComponent
                    className="create-lead-capture-popup-confirmation-description-checkbox-input"
                    style={{
                      maxHeight: "590px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      width: "460px",
                    }}
                    options={{
                      scrollbars: {
                        theme: "os-theme-dark",
                        autoHide: "scroll",
                        autoHideDelay: 400,
                      },
                    }}
                  >
                    <div className="form-group">
                      <DatePickerComponent
                        fieldName={"asOf"}
                        label="As Of"
                        value={values?.asOf}
                        disabled={true}
                        minDate={dayjs()}
                        onChange={(e: any) => {
                          setFieldValue("asOf", e);
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <TextInputComponent
                        type="text"
                        fieldName="Opened By"
                        placeholder="Opened By"
                        label="Opened By"
                        disabled={true}
                        optional={false}
                        value={values.openedBy}
                      />
                      {touched?.openedBy && (
                        <ErrorMessage
                          name={"openedBy"}
                          component="div"
                          className="error-message"
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <div className={`form-text-input-component`}>
                        <InputLabel required={true} htmlFor={"description"}>
                          Description
                        </InputLabel>

                        <FormControl>
                          <Textarea
                            color="neutral"
                            disabled={false}
                            minRows={2}
                            size="lg"
                            variant="outlined"
                            value={values.description}
                            onChange={(e) =>
                              setFieldValue("description", e?.target?.value)
                            }
                            onFocus={() =>
                              setFieldValue("isShowDescription", true)
                            }
                            onBlur={() =>
                              setFieldValue("isShowDescription", false)
                            }
                            placeholder="Enter description here"
                          />
                        </FormControl>
                        <div className="error-message">
                          {typeof errors?.description === "string"
                            ? errors?.description || ""
                            : null}
                        </div>
                        {values.isShowDescription ? (
                          <div
                            className={`char-counter ${
                              remaining < 0 ? "error-message" : ""
                            }`}
                          >
                            {charCount} / 1000 characters
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="form-group">
                      <div className={`form-text-input-component`}>
                        <InputLabel required={true} htmlFor={"mitigationPlan"}>
                          Mitigation Plan
                        </InputLabel>

                        <FormControl>
                          <Textarea
                            color="neutral"
                            disabled={false}
                            minRows={2}
                            size="lg"
                            variant="outlined"
                            value={values.mitigationPlan}
                            onChange={(e) =>
                              setFieldValue("mitigationPlan", e?.target?.value)
                            }
                            onFocus={() =>
                              setFieldValue("isShowMitigationPlan", true)
                            }
                            onBlur={() =>
                              setFieldValue("isShowMitigationPlan", false)
                            }
                            placeholder="Enter mitigation plan here"
                          />
                        </FormControl>
                        <div className="error-message">
                          {typeof errors?.mitigationPlan === "string"
                            ? errors?.mitigationPlan || ""
                            : null}
                        </div>
                        {values.isShowMitigationPlan ? (
                          <div
                            className={`char-counter ${
                              remainingMitigation < 0 ? "error-message" : ""
                            }`}
                          >
                            {charCountMitigation} / 1000 characters
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="form-group form-group-status form-text-input-component">
                      <InputLabel>Status</InputLabel>
                      <Autocomplete<StatusOption>
                        value={values.status}
                        onChange={(event, newValue) => {
                          setFieldValue("status", newValue);
                        }}
                        options={statusList}
                        getOptionLabel={(option) => option.statusName}
                        isOptionEqualToValue={(option, value) =>
                          option.statusId === value.statusId
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label=""
                            placeholder="Select Status"
                          />
                        )}
                        sx={{
                          "& .MuiAutocomplete-option": {
                            fontSize: "14px",
                            fontFamily: "Manrope, sans-serif",
                          },
                        }}
                        slots={{ paper: (props) => slotsPaperStatus(props) }}
                        slotProps={{
                          popper: {
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "14px",
                                fontFamily: "Manrope, sans-serif",
                              },
                            },
                          },
                        }}
                      />
                    </div>
                    <div className="error-message">
                      {typeof errors?.status === "string"
                        ? errors?.status || ""
                        : null}
                    </div>
                  </OverlayScrollbarsComponent>
                  <DialogActions>
                    <div className="action-butn-wrapper">
                      <button
                        onClick={onCancel}
                        className="cancel-btn"
                        type="button"
                      >
                        Cancel
                      </button>
                      {submitCount > 0 && !isValid ? (
                        <Tooltip
                          title="Please resolve all errors before submitting."
                          placement="top"
                        >
                          <span>
                            <button
                              autoFocus
                              className="action-btn"
                              type="submit"
                              disabled
                              style={{
                                opacity: 0.5,
                                cursor: "not-allowed",
                              }}
                            >
                              {addPopupTitle === "Edit Risks/Issues"
                                ? "Save"
                                : "Create"}
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          autoFocus
                          className="action-btn"
                          type="submit"
                          style={{
                            opacity: 1,
                            cursor: "pointer",
                          }}
                        >
                          {addPopupTitle === "Edit Risks/Issues"
                            ? "Save"
                            : "Create"}
                        </button>
                      )}
                    </div>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardInitiativeIssueAddPopup;
